import React, { FunctionComponent, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import {
  matchPath,
  Redirect,
  Route,
  useHistory,
  useLocation
} from 'react-router'
import { useTranslation } from 'react-i18next'

import { ClinTheme } from '../../ClinTheme'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { NotFound } from '../../pages/NotFound'
import { isProgramClosed } from '../../pages/Patients/PatientDetail/PatientDetail.model'
import { getPatientDetail } from '../../services/ApiService'
import { handlePatientNotFound } from '../../utils/handlePatientNotFound'
import { PatientProgramDto } from '../../types/swaggerTypes'
import {
  AnnounceMode,
  ClinAnnounceBar
} from '../ClinAnnounceBar/ClinAnnounceBar'
import { ClinSpinner } from '../ClinSpinner'
import { StyledSpinnerContainer } from '../ClinSpinner/ClinSpinner.styles'

interface IPatientRouteParams {
  physicianId: string
  patientId: string
}

interface IPrivatePatientRouteProps {
  component: FunctionComponent<any>
  exact: boolean
  path: string
}

export const PrivatePatientRoute: FunctionComponent<
  IPrivatePatientRouteProps
> = ({ component, path, exact }) => {
  const [program, setProgram] = useState<PatientProgramDto>()
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const { t } = useTranslation()
  const { dispatch } = useAppContext()
  const location = useLocation()
  const history = useHistory()
  const match = matchPath<IPatientRouteParams>(location.pathname, {
    path: '/programs/my-physicians/:physicianId/:patientId',
    exact: false,
    strict: false
  })
  const physicianId = match?.params.physicianId
  const patientId = match?.params.patientId
  const accessProgramsPath = '/programs/access-programs'
  const patientDetailsPath = `/programs/my-physicians/${physicianId}/${patientId}/`

  useEffect(() => {
    let active = true
    setProgram(undefined)
    setHasError(false)
    setIsLoading(true)
    if (!patientId || !physicianId) {
      setHasError(true)
      setIsLoading(false)
      return
    }
    getPatientDetail(patientId, physicianId)
      .then((response) => {
        if (!active) return
        setProgram(response.data.program ?? undefined)
      })
      .catch((error) => {
        if (!active) return
        if (handlePatientNotFound(error, dispatch, history)) return
        setHasError(true)
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Error,
            `An error occurred while retrieving this patients details. ${error}`
          )
        )
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [dispatch, history, patientId, physicianId])

  if (isLoading) {
    return (
      <Container>
        <Row justify="center">
          <Col width="auto">
            <StyledSpinnerContainer>
              <ClinSpinner size={ClinTheme.space[7]} />
            </StyledSpinnerContainer>
          </Col>
        </Row>
      </Container>
    )
  }

  if (hasError || !program?.programId) {
    return (
      <Container>
        <ClinAnnounceBar mode={AnnounceMode.Error}>
          {t(
            hasError
              ? 'patient_detail:details_unavailable'
              : 'patient_detail:missing_programme'
          )}
        </ClinAnnounceBar>
      </Container>
    )
  }

  return (
    <>
      {!isProgramClosed(program) ? (
        <Route path={path} exact={exact} component={component} />
      ) : (
        <>
          {location.pathname === patientDetailsPath ? (
            <Redirect to={patientDetailsPath} exact={true} />
          ) : (
            <Route
              path={accessProgramsPath}
              exact={true}
              component={NotFound}
            />
          )}
        </>
      )}
    </>
  )
}
